import React, { useRef, useState } from 'react';
import { ShieldCheck, Upload, Loader2, CheckCircle2, Clock, XCircle, IdCard, UserSquare } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const MAX_BYTES = 2 * 1024 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function DocInput({ label, icon: Icon, testid, value, onPick, disabled }) {
  const ref = useRef(null);
  return (
    <div>
      <button
        type="button"
        data-testid={testid}
        disabled={disabled}
        onClick={() => ref.current?.click()}
        className="w-full rounded-xl border border-dashed border-black/15 bg-white hover:border-[#d97757] transition-colors p-4 flex items-center gap-3 text-left disabled:opacity-50"
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${value ? 'bg-emerald-50 text-emerald-600' : 'bg-neutral-100 text-neutral-500'}`}>
          {value ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-medium text-[#1a1d1a]">{label}</div>
          <div className="text-[11.5px] text-neutral-500 truncate">{value ? value : 'JPG, PNG ou WebP · max 2 Mo'}</div>
        </div>
      </button>
      <input
        ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }}
      />
    </div>
  );
}

export function KycUploadCard({ cagnotte, onSubmitted }) {
  const { toast } = useToast();
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const status = cagnotte.kycStatus || 'none';

  if (status === 'verified') {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5" data-testid="kyc-verified-card">
        <div className="flex items-center gap-2 text-emerald-700">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-[14px] font-semibold">Identité vérifiée</span>
        </div>
        <p className="mt-1 text-[12.5px] text-emerald-700/80">Vous pouvez retirer les fonds de cette cagnotte.</p>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5" data-testid="kyc-pending-card">
        <div className="flex items-center gap-2 text-amber-700">
          <Clock className="w-5 h-5" />
          <span className="text-[14px] font-semibold">Vérification en cours</span>
        </div>
        <p className="mt-1 text-[12.5px] text-amber-700/80">Vos documents sont en cours d'examen. Vous serez autorisé à retirer les fonds une fois validé.</p>
      </div>
    );
  }

  const pick = (setter) => async (file) => {
    if (file.size > MAX_BYTES) { toast({ title: 'Fichier trop lourd', description: 'Maximum 2 Mo par document.' }); return; }
    setter({ file, name: file.name });
  };

  const submit = async () => {
    if (!idFile || !selfieFile || submitting) return;
    setSubmitting(true);
    try {
      const [idData, selfieData] = await Promise.all([fileToDataUrl(idFile.file), fileToDataUrl(selfieFile.file)]);
      await api.post('/kyc/upload', { data: idData, cagnotteId: cagnotte.id });
      await api.post('/kyc/upload', { data: selfieData, cagnotteId: cagnotte.id });
      toast({ title: 'Documents envoyés', description: 'Votre vérification d\u2019identité est en cours d\u2019examen.' });
      onSubmitted?.();
    } catch (e) {
      toast({ title: 'Échec de l\u2019envoi', description: e?.response?.data?.detail || 'Réessayez.' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 sm:p-6" data-testid="kyc-upload-card">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#1a1d1a] text-white flex items-center justify-center"><ShieldCheck className="w-4.5 h-4.5" /></div>
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase font-medium text-[#d97757]">Vérification requise</div>
          <h3 className="text-[16px] font-semibold text-[#1a1d1a] leading-tight">Vérifiez votre identité pour retirer</h3>
        </div>
      </div>
      {status === 'rejected' && (
        <div className="mt-3 flex items-center gap-2 text-[12.5px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="kyc-rejected-note">
          <XCircle className="w-4 h-4" /> Vérification refusée. Merci de renvoyer des documents lisibles.
        </div>
      )}
      <p className="mt-3 text-[12.5px] text-neutral-600 leading-relaxed">
        Cette cagnotte dépasse le seuil réglementaire. Envoyez une photo de votre <b>pièce d'identité</b> et un <b>selfie</b>. Vos documents sont <b>chiffrés</b>, accessibles uniquement par nos agents de vérification, et supprimés après décision.
      </p>
      <div className="mt-4 space-y-3">
        <DocInput label="Pièce d'identité" icon={IdCard} testid="kyc-id-input" value={idFile?.name} onPick={pick(setIdFile)} disabled={submitting} />
        <DocInput label="Selfie" icon={UserSquare} testid="kyc-selfie-input" value={selfieFile?.name} onPick={pick(setSelfieFile)} disabled={submitting} />
      </div>
      <button
        type="button"
        data-testid="kyc-submit-btn"
        onClick={submit}
        disabled={!idFile || !selfieFile || submitting}
        className="btn-anim mt-4 w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white text-[13.5px] font-medium flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        Envoyer pour vérification
      </button>
    </div>
  );
}
